import axios, { AxiosRequestConfig, AxiosResponse, Canceler } from 'axios';
import { ResponseObj } from '../interface';
import AuthService from './auth.service';
import { getUrl } from 'shared/constants/constants';
const axiosInstance = axios.create({ timeout: 2 * 60 * 1000 });
export const cancelToken = axios.CancelToken;
let cancel_req: any;

export { cancel_req };

export interface AxiosParams extends MiscellaneousRequestParams {
	method: string;
	url: string;
	data?: any;
}

export interface MiscellaneousRequestParams {
	isAccessTokenRequire?: boolean;
	contentType?: string;
	responseType?: string;
	xApi?: number | string | null;
	noCache?: boolean;
	dedupeInFlight?: boolean;
	executor?: (cancel: Canceler) => void;
	authToken?: string;
	cacheTtlMs?: number;
	invalidateExact?: InvalidateExactOptions | InvalidateExactOptions[];
	invalidatePrefixes?: string | string[];
	invalidateScopes?: string | string[];
}

type InvalidateExactOptions = {
	url: string;
	method?: string;
	authToken?: string;
};

/**
 * get method
 * @param request object containing axios params
 */
const get = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) =>
	commonAxios({ method: 'GET', url: getUrl(url, params), ...otherData });

/**
 * post method
 * @param request object containing axios params
 */
const post = (url: string, params: any = {}, queryParams = {}, otherData: MiscellaneousRequestParams = {}) =>
	commonAxios({
		method: 'POST',
		url: getUrl(url, queryParams),
		data: params,
		...otherData
	});

/**
 * put method
 * @param request object containing axios params
 */
const put = (url: string, params: any = {}, queryParams = {}, otherData: MiscellaneousRequestParams = {}) =>
	commonAxios({
		method: 'PUT',
		url: getUrl(url, queryParams),
		data: params,
		...otherData
	});

/**
 * deleteRequest method
 * @param request object containing axios params
 */
const deleteRequest = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) =>
	commonAxios({
		method: 'DELETE',
		url: getUrl(url),
		data: params,
		...otherData
	});

/**
 * patch method
 * @param request object containing axios params
 */
const patch = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) =>
	commonAxios({
		method: 'PATCH',
		url: getUrl(url),
		data: params,
		...otherData
	});

/**
 * commonAxios
 * @param object containing method, url, data, access token, content-type
 */

let currentTime = '';
const responseCache = new Map<string, { expiresAt: number; value: any }>();
const pendingRequests = new Map<string, Promise<any>>();
const featureScopePrefixes: Record<string, string[]> = {
	admin_news: ['admin/news', 'admin/permissions/newsList'],
	admin_materials: ['admin/material', 'admin/permissions/courselist'],
	admin_forecast: ['admin/forecast', 'admin/permissions/foreCast'],
	admin_pdf: ['admin/pdfModule', 'admin/permissions/pdfModule'],
	whitelabel_news: ['admin/whitelabel/news', 'admin/whitelabel/all_news'],
	whitelabel_materials: ['admin/whitelabel/add-material-list', 'admin/whitelabel/drag-drop-materials-list', 'admin/material/all_materials'],
	dashboard: ['dashboard/'],
	news: ['news/'],
	materials: ['materials/']
};

const dashboardMutationUrlPatterns = [
	'dashboard/add',
	'dashboard/addCourseListToDashboard',
	'dashboard/alerttodashboard',
	'dashboard/changeviewfornews',
	'costmodel/add_to_dashboard'
];
const dashboardMutationFallbackPatterns = ['/dashboard/', '/dashboard?', 'costmodel/add_to_dashboard', 'costmodel/delete_from_dashboard'];

const buildCacheKey = (method: string, url: string, headers: Record<string, string>) => {
	const token = headers['x-access-token'] || headers['authorization'] || '';
	return `${method}:${url}:${token}`;
};

const hashString = (value: string): string => {
	let hash = 0;
	for (let i = 0; i < value.length; i++) {
		hash = (hash << 5) - hash + value.charCodeAt(i);
		hash |= 0;
	}
	return String(hash);
};

const buildPendingRequestKey = (method: string, url: string, headers: Record<string, string>, body: unknown) => {
	const token = headers['x-access-token'] || headers['authorization'] || '';
	let serializedBody = '';
	if (typeof body === 'string') {
		serializedBody = body;
	} else if (body == null) {
		serializedBody = '';
	} else {
		try {
			serializedBody = JSON.stringify(body);
		} catch {
			serializedBody = String(body);
		}
	}
	return `${method}:${url}:${token}:${hashString(serializedBody)}`;
};

const noClientCacheUrlPatterns = [
	'admin/group/user-details',
	'admin/backpack/list',
	'admin/permissions/courselist',
	'admin/permissions/material',
	'user/profile/user_personlization',
	'user/profile/show_partner_section',
	'user/profile/dashboard_widgets_view'
];

const shouldBypassClientCacheForUrl = (url: string) => noClientCacheUrlPatterns.some((pattern) => url.includes(pattern));

const appendCacheBuster = (url: string) => {
	try {
		const parsedUrl = new URL(url, window.location.origin);
		parsedUrl.searchParams.set('_ts', String(Date.now()));
		return parsedUrl.toString();
	} catch {
		const separator = url.includes('?') ? '&' : '?';
		return `${url}${separator}_ts=${Date.now()}`;
	}
};

const clearByPredicate = (predicate: (key: string) => boolean) => {
	Array.from(responseCache.keys()).forEach((key) => {
		if (predicate(key)) {
			responseCache.delete(key);
		}
	});
	Array.from(pendingRequests.keys()).forEach((key) => {
		if (predicate(key)) {
			pendingRequests.delete(key);
		}
	});
};

const invalidateCacheExact = ({ url, method = 'GET', authToken }: InvalidateExactOptions) => {
	const normalizedMethod = method.toUpperCase();
	if (authToken) {
		const exactKey = `${normalizedMethod}:${url}:${authToken}`;
		clearByPredicate((key) => key === exactKey || key.startsWith(`${exactKey}:`));
		return;
	}
	const keyPrefix = `${normalizedMethod}:${url}:`;
	clearByPredicate((key) => key.startsWith(keyPrefix));
};

const invalidateCacheByPrefix = (prefixes: string | string[]) => {
	const normalizedPrefixes = (Array.isArray(prefixes) ? prefixes : [prefixes]).filter(Boolean);
	if (!normalizedPrefixes.length) {
		return;
	}
	clearByPredicate((key) => normalizedPrefixes.some((prefix) => key.includes(prefix)));
};

const invalidateFeatureScope = (scope: string) => {
	const prefixes = featureScopePrefixes[scope];
	if (!prefixes?.length) {
		return;
	}
	invalidateCacheByPrefix(prefixes);
};

const clearCache = () => {
	responseCache.clear();
	pendingRequests.clear();
};

const runInvalidations = ({
	invalidateExact,
	invalidatePrefixes,
	invalidateScopes
}: Pick<MiscellaneousRequestParams, 'invalidateExact' | 'invalidatePrefixes' | 'invalidateScopes'>) => {
	const exactInvalidations = Array.isArray(invalidateExact) ? invalidateExact : invalidateExact ? [invalidateExact] : [];
	exactInvalidations.forEach((entry) => invalidateCacheExact(entry));
	if (invalidatePrefixes) {
		invalidateCacheByPrefix(invalidatePrefixes);
	}
	const scopes = Array.isArray(invalidateScopes) ? invalidateScopes : invalidateScopes ? [invalidateScopes] : [];
	scopes.forEach((scope) => invalidateFeatureScope(scope));
};

const clearDashboardLocalStorageCaches = () => {
	try {
		if (typeof window === 'undefined' || !window.localStorage) {
			return;
		}
		Object.keys(window.localStorage).forEach((key) => {
			if (
				key.startsWith('dashboard-cache:v1:') ||
				key.startsWith('dashboard-announcements:v1:') ||
				key.startsWith('dashboard-news-widget:v1:')
			) {
				window.localStorage.removeItem(key);
			}
		});
	} catch {
		// Ignore localStorage access errors.
	}
};

const emitDashboardRefreshEvent = () => {
	try {
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new CustomEvent('dashboard:refresh-requested'));
		}
	} catch {
		// Ignore event dispatch issues.
	}
};

const resolveAutoInvalidateScopes = (method: string, url: string, invalidateScopes?: string | string[]): string[] => {
	const explicitScopes = Array.isArray(invalidateScopes) ? invalidateScopes : invalidateScopes ? [invalidateScopes] : [];
	if (method === 'GET') {
		return explicitScopes;
	}
	const normalizedUrl = url.toLowerCase();
	const shouldInvalidateDashboard =
		dashboardMutationUrlPatterns.some((pattern) => normalizedUrl.includes(pattern)) ||
		dashboardMutationFallbackPatterns.some((pattern) => normalizedUrl.includes(pattern));
	if (!shouldInvalidateDashboard) {
		return explicitScopes;
	}
	return Array.from(new Set([...explicitScopes, 'dashboard']));
};

const commonAxios = ({
	method,
	url,
	data,
	isAccessTokenRequire = true,
	contentType = 'application/json',
	responseType,
	xApi = '',
	noCache = false,
	dedupeInFlight = true,
	authToken,
	executor = (c) => (cancel_req = c),
	cacheTtlMs = 30000,
	invalidateExact,
	invalidatePrefixes,
	invalidateScopes
}: AxiosParams): Promise<any> => {
	currentTime = new Date().toISOString();
	const headers: any = {
		'Content-Type': contentType
	};
	const token = isAccessTokenRequire && AuthService.getAccessToken();
	if (xApi) {
		headers['x-api-key'] = `${xApi}`;
	}
	if (noCache) {
		headers['Cache-Control'] = 'no-cache, no-store, must-revalidate, max-age=0';
	}
	if (token) {
		headers['x-access-token'] = `${token}`;
	} else if (authToken) {
		headers['x-access-token'] = `${authToken}`;
	} else {
		// headers['x-request-language'] = localStorage.getItem('lang');
	}
	let body: any = null;
	if (contentType === 'application/json') {
		body = JSON.stringify(data);
	} else {
		body = data;
	}
	const shouldCache = method === 'GET' && !noCache && cacheTtlMs > 0 && responseType !== 'arraybuffer' && responseType !== 'blob';
	const shouldBypassClientCache = shouldBypassClientCacheForUrl(url);
	const effectiveUrl = shouldBypassClientCache ? appendCacheBuster(url) : url;
	if (shouldBypassClientCache) {
		headers['Cache-Control'] = 'no-cache, no-store, must-revalidate, max-age=0';
		headers['Pragma'] = 'no-cache';
		headers['Expires'] = '0';
	}
	const effectiveShouldCache = shouldCache && !shouldBypassClientCache;
	const shouldDedupeInFlight = dedupeInFlight && !noCache && !shouldBypassClientCache;
	const cacheKey = shouldCache ? buildCacheKey(method, effectiveUrl, headers) : '';
	if (effectiveShouldCache) {
		const cached = responseCache.get(cacheKey);
		if (cached && cached.expiresAt > Date.now()) {
			return Promise.resolve(cached.value);
		}
		if (cached) {
			responseCache.delete(cacheKey);
		}
	}
	const pendingKey = buildPendingRequestKey(method, effectiveUrl, headers, body);
	if (shouldDedupeInFlight) {
		const existingRequest = pendingRequests.get(pendingKey);
		if (existingRequest) {
			return existingRequest;
		}
	}

	const requestPromise = new Promise((resolve, reject) => {
		axiosInstance({
			method: method,
			url: effectiveUrl,
			cancelToken: new axios.CancelToken(executor),
			headers: headers,
			responseType: responseType,
			data: body
		} as AxiosRequestConfig)
			.then((response: AxiosResponse<ResponseObj<any>>) => {
				if (response) {
					if (contentType === 'application/json') {
						const resolved = response.data instanceof ArrayBuffer ? response : response.data.data;
						if (effectiveShouldCache) {
							responseCache.set(cacheKey, { expiresAt: Date.now() + cacheTtlMs, value: resolved });
						}
						if (method !== 'GET') {
							const autoInvalidateScopes = resolveAutoInvalidateScopes(method, url, invalidateScopes);
							runInvalidations({ invalidateExact, invalidatePrefixes, invalidateScopes: autoInvalidateScopes });
							if (autoInvalidateScopes.includes('dashboard')) {
								clearDashboardLocalStorageCaches();
								emitDashboardRefreshEvent();
							}
						}
						resolve(resolved);
					} else {
						if (effectiveShouldCache) {
							responseCache.set(cacheKey, { expiresAt: Date.now() + cacheTtlMs, value: response.data });
						}
						if (method !== 'GET') {
							const autoInvalidateScopes = resolveAutoInvalidateScopes(method, url, invalidateScopes);
							runInvalidations({ invalidateExact, invalidatePrefixes, invalidateScopes: autoInvalidateScopes });
							if (autoInvalidateScopes.includes('dashboard')) {
								clearDashboardLocalStorageCaches();
								emitDashboardRefreshEvent();
							}
						}
						resolve(response.data);
					}
				} else {
					resolve({ reqCanceled: true });
				}
			})
			.catch((error: Error) => {
				reject(error);
			});
	});
	if (shouldDedupeInFlight) {
		pendingRequests.set(pendingKey, requestPromise);
		return requestPromise.finally(() => {
			pendingRequests.delete(pendingKey);
		});
	}

	return requestPromise;
};

const getBlob = (url: string) =>
	new Promise((resolve, reject) => {
		axios
			.get(url, { responseType: 'blob' })
			.then((response: any) => {
				resolve(response.data);
			})
			.catch((error: Error) => reject(error));
	});

const httpService = {
	get: get,
	post: post,
	put: put,
	deleteRequest: deleteRequest,
	patch: patch,
	getBlob: getBlob,
	invalidateCacheExact: invalidateCacheExact,
	invalidateCacheByPrefix: invalidateCacheByPrefix,
	invalidateFeatureScope: invalidateFeatureScope,
	clearDashboardLocalStorageCaches: clearDashboardLocalStorageCaches,
	requestDashboardRefresh: emitDashboardRefreshEvent,
	clearCache: clearCache,
	get currentTime() {
		return currentTime;
	}
};

export { axiosInstance };

export default httpService;
